import type { ChartOptions, TooltipItem } from 'chart.js';
import type { Context as DataLabelsContext } from 'chartjs-plugin-datalabels';

import { formatBigNumber, formatMoney } from 'utils';

type MaybeArray<T> = T | T[];

const getNumberValue = (value: unknown): number => {
  if (typeof value === 'number') return value;

  if (
    value &&
    typeof value === 'object' &&
    'y' in value &&
    typeof (value as { y?: unknown }).y === 'number'
  ) {
    return (value as { y: number }).y;
  }

  return 0;
};

const getTooltipTitle = (item: TooltipItem<'bar'> | TooltipItem<'line'>) => {
  const labels = item.chart.data.labels;
  if (!labels) return item.label;

  const label = labels[item.dataIndex] as MaybeArray<string>;
  if (!label) return item.label;

  return Array.isArray(label) ? label.join(', ') : label;
};

const commonOptions = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },

    tooltip: {
      mode: 'index' as const,
      boxPadding: 4,
      titleFont: { size: 12 },

      callbacks: {
        title(items: TooltipItem<'bar'>[] | TooltipItem<'line'>[]) {
          return getTooltipTitle(items[0]);
        },

        label(item: TooltipItem<'bar'> | TooltipItem<'line'>) {
          const value = getNumberValue(item.raw);
          return `${item.dataset.label}: ${formatMoney(value)}`;
        },
      },
    },
  },
  scales: {
    x: {
      ticks: {
        color: '#333333',

        callback(
          this: { chart: { data: { labels?: unknown[] } } },
          tickValue: string | number,
          index: number,
        ) {
          const labels = this.chart.data.labels;
          if (!labels) return tickValue;

          const label = labels[index] as MaybeArray<string>;
          if (!label) return tickValue;
          if (typeof label === 'string') return label;

          return label.map((v, index) => {
            if (v.endsWith(',') || index + 1 === label.length) return v;
            return `${v},`;
          });
        },
      },
      grid: {
        borderDash: [5, 5],
        color: '#cccccc',
        tickWidth: 0,
        tickLength: 2,
        drawBorder: false,
      },
    },
    y: {
      grace: '25%',
      beginAtZero: true,
      ticks: {
        color: '#333333',

        callback(label: string | number) {
          return typeof label === 'string' ? label : formatBigNumber(label, 2);
        },
      },
      grid: {
        borderDash: [5, 5],
        color: '#cccccc',
        tickWidth: 0,
        tickLength: 12,
        drawBorder: false,
      },
    },
  },
};

export const barOptions: ChartOptions<'bar'> = {
  ...commonOptions,
  plugins: {
    ...commonOptions.plugins,

    datalabels: {
      anchor: 'end',
      align: 'end',
      offset: 4,
      color: '#333333',
      font: { size: 12 },

      formatter(value: unknown, context: DataLabelsContext) {
        let total = getNumberValue(value);

        context.chart.data.datasets.forEach((dataset) => {
          if (dataset === context.dataset) return;

          total += getNumberValue(dataset.data[context.dataIndex]);
        });

        return formatMoney(total);
      },

      display(context: DataLabelsContext) {
        const currentOrder = context.dataset.order;
        if (currentOrder === undefined) return true;

        let highestOrder = 0;

        context.chart.data.datasets.forEach((dataset) => {
          if (dataset.order !== undefined && dataset.order > highestOrder) {
            highestOrder = dataset.order;
          }
        });

        return currentOrder >= highestOrder;
      },
    },
  },
};

export const lineOptions: ChartOptions<'line'> = {
  ...commonOptions,
  scales: {
    ...commonOptions.scales,
    x: {
      ...commonOptions.scales.x,
      offset: true,
      grid: {
        ...commonOptions.scales.x.grid,
        offset: true,
        color: '#e6e6e6',
        lineWidth: 1,
      },
    },
    y: {
      ...commonOptions.scales.y,
      grid: {
        ...commonOptions.scales.y.grid,
        color(context) {
          return context.tick.value === 0 ? '#999999' : '#cccccc';
        },
        lineWidth(context) {
          return context.tick.value === 0 ? 2 : 1;
        },
      },
    },
  },
  plugins: {
    ...commonOptions.plugins,

    datalabels: {
      anchor: 'end',
      align: 'end',
      offset: 4,
      color: '#333333',
      font: { size: 12 },

      formatter(value: unknown) {
        return formatMoney(getNumberValue(value));
      },
    },
  },
};